import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableColumn } from "./data-table";
import { useColumnWidths } from "@/hooks/useColumnWidths";
import { SKELETON_COLUMN_PREFIX } from "./skeleton-columns";

interface SkeletonTableBodyProps {
  columsNumber: number;
  rowsNumber: number;
  columns?: TableColumn[];
  enableRowSelection?: boolean;
  tableId?: string;
}

const RowSkeleton = ({
  columsNumber,
  enableRowSelection = false,
  columns,
  getWidth,
}: {
  columsNumber: number;
  enableRowSelection?: boolean;
  columns?: TableColumn[];
  getWidth?: (key: string) => number | undefined;
}) => (
  <TableRow className="h-9">
    {enableRowSelection && (
      <TableCell className="w-10 py-0">
        <Skeleton className="h-4 w-4 rounded" />
      </TableCell>
    )}
    {columns && columns.length > 0
      ? columns.map((column) => {
          const w = getWidth?.(column.key);
          const style: React.CSSProperties | undefined =
            typeof w === "number"
              ? { width: `${w}px`, minWidth: `${w}px` }
              : undefined;
          return (
            <TableCell
              key={column.key}
              data-column-key={column.key}
              className={`py-0 ${column.className ?? ""}`}
              style={style}
            >
              <Skeleton className="h-4 w-20" />
            </TableCell>
          );
        })
      : Array.from({ length: columsNumber }).map((_, index) => (
          <TableCell key={index} className="py-0">
            <Skeleton className="h-4 w-20" />
          </TableCell>
        ))}
    <TableCell className="py-0 text-right">
      <Skeleton className="h-4 w-6 rounded ml-auto" />
    </TableCell>
  </TableRow>
);

/**
 * Skeleton del CUERPO de la tabla (sin Card/header). Se renderiza dentro de la
 * card principal real para mantener el header consistente entre estados.
 */
export default function SkeletonTableBody({
  columsNumber,
  rowsNumber,
  columns,
  enableRowSelection = false,
  tableId,
}: SkeletonTableBodyProps) {
  const { getWidth } = useColumnWidths(tableId);
  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0">
      <div className="inline-block min-w-full align-middle">
        <Table>
          <TableHeader className="rounded-t-lg">
            <TableRow>
              {enableRowSelection && (
                <TableHead className="w-10">
                  <Skeleton className="h-4 w-4 rounded" />
                </TableHead>
              )}
              {columns
                ? columns.map((column) => {
                    const w = getWidth(column.key);
                    const style: React.CSSProperties | undefined =
                      typeof w === "number"
                        ? { width: `${w}px`, minWidth: `${w}px` }
                        : undefined;
                    return (
                      <TableHead
                        key={column.key}
                        data-column-key={column.key}
                        className={`text-muted-foreground text-sm font-normal ${column.className ?? ""}`}
                        style={style}
                      >
                        {column.key.startsWith(SKELETON_COLUMN_PREFIX) ? (
                          <Skeleton className="h-4 w-20" />
                        ) : (
                          <div className="flex items-center gap-2 select-none">
                            <span>{column.label}</span>
                          </div>
                        )}
                      </TableHead>
                    );
                  })
                : Array.from({ length: columsNumber }).map((_, index) => (
                    <TableHead key={index}>
                      <Skeleton className="h-4 w-20" />
                    </TableHead>
                  ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rowsNumber }).map((_, index) => (
              <RowSkeleton
                key={index}
                columsNumber={columsNumber}
                enableRowSelection={enableRowSelection}
                columns={columns}
                getWidth={getWidth}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
